import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-accent text-accent-foreground hover:bg-accent/90 shadow-elevation-1 hover:shadow-elevation-2",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-elevation-1",
        outline:
          "border border-white/20 bg-white/8 backdrop-blur-xl hover:bg-white/12 hover:border-white/30 text-foreground",
        secondary:
          "bg-white/5 text-foreground hover:bg-white/10 backdrop-blur-lg",
        ghost: "hover:bg-white/8 text-foreground/80 hover:text-foreground",
        link: "text-accent underline-offset-4 hover:underline",
        glass: "glass-panel text-foreground hover:glass-panel-strong",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90 accent-glow hover:accent-glow-strong",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
